import { AudioProxyOptions, StreamInfo, Environment } from './types';

export class AudioProxyClient {
  private options: Required<AudioProxyOptions>;
  private environment: Environment;

  constructor(options?: AudioProxyOptions) {
    this.options = {
      proxyUrl: options?.proxyUrl || 'http://localhost:3001',
      autoDetect: options?.autoDetect ?? true,
      fallbackToOriginal: options?.fallbackToOriginal ?? true,
      retryAttempts: options?.retryAttempts || 3,
      retryDelay: options?.retryDelay || 1000,
    };

    this.environment = this.detectEnvironment();
  }

  private detectEnvironment(): Environment {
    if (typeof window === 'undefined') {
      return 'unknown';
    }

    if ((window as any).__TAURI__) {
      return 'tauri';
    }

    if ((window as any).electron || (window as any).process?.versions?.electron) {
      return 'electron';
    }

    return 'web';
  }

  public getEnvironment(): Environment {
    return this.environment;
  }

  public async isProxyAvailable(): Promise<boolean> {
    try {
      const response = await fetch(`${this.options.proxyUrl}/health`);
      return response.ok;
    } catch {
      return false;
    }
  }

  public async canPlayUrl(url: string): Promise<StreamInfo> {
    // Check if it's a local file or data URL
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return {
        url,
        status: 200,
        headers: {},
        canPlay: true,
        requiresProxy: false,
      };
    }

    // In development or web environment, external URLs need proxy
    const isDevelopment = this.isDevelopmentEnvironment();
    const needsProxy = this.environment === 'web' || isDevelopment;

    if (needsProxy) {
      const proxyAvailable = await this.isProxyAvailable();
      return {
        url,
        status: proxyAvailable ? 200 : 0,
        headers: {},
        canPlay: proxyAvailable,
        requiresProxy: true,
      };
    }

    // In production desktop apps, can play directly
    return {
      url,
      status: 200,
      headers: {},
      canPlay: true,
      requiresProxy: false,
    };
  }

  public async getPlayableUrl(originalUrl: string): Promise<string> {
    // Handle local files and data URLs
    if (!originalUrl.startsWith('http://') && !originalUrl.startsWith('https://')) {
      return this.handleLocalUrl(originalUrl);
    }

    // Check if we need proxy
    const streamInfo = await this.canPlayUrl(originalUrl);
    
    if (!streamInfo.requiresProxy) {
      return originalUrl;
    }

    // Try to use proxy with retries
    for (let attempt = 0; attempt < this.options.retryAttempts; attempt++) {
      try {
        const proxyAvailable = await this.isProxyAvailable();
        
        if (proxyAvailable) {
          const proxiedUrl = `${this.options.proxyUrl}/proxy?url=${encodeURIComponent(originalUrl)}`;
          
          // Verify the proxy URL works
          const response = await fetch(proxiedUrl, { method: 'HEAD' });
          if (response.ok) {
            return proxiedUrl;
          }
        }
      } catch (error) {
        console.warn(`[AudioProxyClient] Proxy attempt ${attempt + 1} failed:`, error);
        
        if (attempt < this.options.retryAttempts - 1) {
          await this.delay(this.options.retryDelay);
        }
      }
    }

    // Fallback to original URL if configured
    if (this.options.fallbackToOriginal) {
      console.warn('[AudioProxyClient] Falling back to original URL');
      return originalUrl;
    }

    throw new Error('Failed to get playable URL: Proxy not available');
  }

  private handleLocalUrl(url: string): string {
    // Handle Tauri convertFileSrc
    if (this.environment === 'tauri' && (window as any).__TAURI__) {
      const cleanPath = url.replace('file://', '');
      return (window as any).__TAURI__.convertFileSrc(cleanPath);
    }

    // Handle Electron file protocol
    if (this.environment === 'electron') {
      if (url.startsWith('/') || url.match(/^[A-Za-z]:\\/)) {
        return `file://${url}`;
      }
    }

    return url;
  }

  private isDevelopmentEnvironment(): boolean {
    if (typeof window === 'undefined') return false;
    
    const hostname = window.location?.hostname || '';
    const port = window.location?.port || '';
    
    return hostname === 'localhost' || 
           hostname === '127.0.0.1' || 
           port === '3000' || 
           port === '8080' ||
           port === '5173';
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  public enableDebug(): void {
    if (typeof window !== 'undefined') {
      (window as any).__AUDIO_PROXY_DEBUG__ = true;
    }
  }
}

// Convenience function
export function createAudioClient(options?: AudioProxyOptions): AudioProxyClient {
  return new AudioProxyClient(options);
}